import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ClientDocumentCountService from '../../Services/ClientDocumentCount.service';

// Async thunk for fetching document counts
export const fetchDocumentCounts = createAsyncThunk(
  'documentCounts/fetch',
  async ({ accountantId, token, page, pageSize, isActive = true }, { rejectWithValue }) => {
    try {
      const documentCounts = await ClientDocumentCountService.getTotalCountClientDocumentCountsByAccountantId(
        accountantId,
        token,
        page,
        pageSize,
        isActive
      );
      return {
        data: documentCounts,
        accountantId,
        page,
        pageSize,
        isActive,
        timestamp: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Stores document counts by accountantId
  counts: {},
  // Loading states by accountantId
  loading: {},
  // Error states by accountantId
  errors: {},
  // Cache metadata
  cache: {
    // Format: { [accountantId]: { timestamp, page, pageSize } }
  },
  // Cache duration in milliseconds (2 minutes - matches mainPageDataSlice)
  cacheDuration: 2 * 60 * 1000,
};

const documentCountsSlice = createSlice({
  name: 'documentCounts',
  initialState,
  reducers: {
    // Clear cache for specific accountant
    clearCache: (state, action) => {
      const { accountantId } = action.payload;
      if (accountantId) {
        delete state.counts[accountantId];
        delete state.cache[accountantId];
        delete state.loading[accountantId];
        delete state.errors[accountantId];
      } else {
        // Clear all cache
        state.counts = {};
        state.cache = {};
        state.loading = {};
        state.errors = {};
      }
    },
    // Update specific client count (for real-time updates via SignalR)
    updateClientCount: (state, action) => {
      const { accountantId, caseId, totalCounts } = action.payload;
      if (state.counts[accountantId]) {
        const clientIndex = state.counts[accountantId].findIndex(c => c.caseId === caseId);
        if (clientIndex !== -1) {
          state.counts[accountantId][clientIndex].totalCounts = totalCounts;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentCounts.pending, (state, action) => {
        const { accountantId } = action.meta.arg;
        state.loading[accountantId] = true;
        state.errors[accountantId] = null;
      })
      .addCase(fetchDocumentCounts.fulfilled, (state, action) => {
        const { data, accountantId, page, pageSize, isActive, timestamp } = action.payload;
        state.counts[accountantId] = data;
        state.loading[accountantId] = false;
        state.cache[accountantId] = {
          timestamp,
          page,
          pageSize,
          isActive
        };
      })
      .addCase(fetchDocumentCounts.rejected, (state, action) => {
        const { accountantId } = action.meta.arg;
        state.loading[accountantId] = false;
        state.errors[accountantId] = action.payload;
      });
  },
});

// Selectors
export const selectDocumentCounts = (state, accountantId) => 
  state.documentCountsSlice?.counts[accountantId] || [];

export const selectDocumentCountsLoading = (state, accountantId) => 
  state.documentCountsSlice?.loading[accountantId] || false;

export const selectDocumentCountsError = (state, accountantId) => 
  state.documentCountsSlice?.errors[accountantId] || null;

export const selectIsCacheValid = (state, accountantId) => {
  const cache = state.documentCountsSlice?.cache[accountantId];
  if (!cache) return false;
  
  const now = Date.now();
  const cacheAge = now - cache.timestamp;
  return cacheAge < state.documentCountsSlice.cacheDuration;
};

export const { clearCache, updateClientCount } = documentCountsSlice.actions;

export default documentCountsSlice.reducer;

